/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  WbBreadcrumb,
  WbBreadcrumbs,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { Content } from '@mainstage/core-components';
import { useState } from 'react';
import { FieldExtensionOptions } from '../../extensions/types';
import type { LayoutOptions } from '../../layouts';
import {
  TemplateDirectoryAccess,
  WebFileSystemAccess,
} from '../../lib/filesystem';
import { TemplateEditor } from './TemplateEditor';
import { TemplateEditorIntro } from './TemplateEditorIntro';
import { TemplateFormPreviewer } from './TemplateFormPreviewer';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { templateIndexRouteRef } from '../../routes';
import { WbPage } from '@agilelab/plugin-wb-platform';

type Selection =
  | {
      type: 'local';
      directory: TemplateDirectoryAccess;
    }
  | {
      type: 'form';
    };

interface TemplateEditorPageProps {
  defaultPreviewTemplate?: string;
  customFieldExtensions?: FieldExtensionOptions<any, any>[];
  layouts?: LayoutOptions[];
}

export function TemplateEditorPage(props: TemplateEditorPageProps) {
  const [selection, setSelection] = useState<Selection>();
  const adminTemplateIndexRoute = useRouteRef(templateIndexRouteRef);
  let content: JSX.Element | null = null;
  if (selection?.type === 'local') {
    content = (
      <TemplateEditor
        directory={selection.directory}
        fieldExtensions={props.customFieldExtensions}
        onClose={() => setSelection(undefined)}
        layouts={props.layouts}
      />
    );
  } else if (selection?.type === 'form') {
    content = (
      <TemplateFormPreviewer
        defaultPreviewTemplate={props.defaultPreviewTemplate}
        customFieldExtensions={props.customFieldExtensions}
        onClose={() => setSelection(undefined)}
        layouts={props.layouts}
      />
    );
  } else {
    content = (
      <Content>
        <TemplateEditorIntro
          onSelect={option => {
            if (option === 'local') {
              WebFileSystemAccess.requestDirectoryAccess()
                .then(directory => setSelection({ type: 'local', directory }))
                .catch(() => {});
            } else if (option === 'form') {
              setSelection({ type: 'form' });
            }
          }}
        />
      </Content>
    );
  }

  return (
    <WbPage themeId="home">
      <WbHeader
        title="Template Studio"
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Administration</WbBreadcrumb>
            <WbBreadcrumb to={adminTemplateIndexRoute()}>
              Templates
            </WbBreadcrumb>
            <WbBreadcrumb>Template Studio</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      {content}
    </WbPage>
  );
}
